import React, { useCallback, useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Truck, ArrowRight } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { Logo, LOGOS_HIDDEN } from './Logo';
import { useCart } from './CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
import { useT } from '../utils/i18n';
interface NavbarProps {
  isDark: boolean;
  toggleDark: () => void;
}
export function Navbar({
  isDark,
  toggleDark
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [logoRipple, setLogoRipple] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [navRetracted, setNavRetracted] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const lastScrollY = useRef(0);
  // Reset mobile dropdowns when menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileShopOpen(false);
      setMobileHelpOpen(false);
      setMobileAboutOpen(false);
    }
  }, [isOpen]);
  useEffect(() => {
    const onResize = () => setIsMobileNav(window.innerWidth < 768);
    window.addEventListener('resize', onResize, {
      passive: true
    });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // Mobile retract: slide up 50% when scrolling down, restore at top
      const isMobile = window.innerWidth < 1300;
      if (!isMobile) {
        setNavRetracted(false);
      } else if (y === 0) {
        setNavRetracted(false);
      } else if (y > lastScrollY.current && y > 60) {
        setNavRetracted(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  const navLinkClass = 'text-stone-800 dark:text-white';
  const dropdownLinkClass = 'text-stone-600 dark:text-stone-300';
  // Category labels in the Shop Materials menu use the brand green in
  // BOTH light and dark modes — matches the visual treatment customers
  // already see for the green "LIVE", "Pay on delivery", and pill chips
  // throughout the site, so the Shop section reads as on-brand instead
  // of a muted secondary palette. Contractor pages override to
  // brand-orange to preserve the contractor-specific visual identity.
  const categoryLabelClass = isContractorPage ? 'text-brand-orange' : 'text-[#17A347] dark:text-[#22c95c]';
  const {
    totalItems,
    totalPrice,
    toggleCart
  } = useCart();
  const t = useT();
  // Helper to handle anchor links from other pages
  const getAnchorHref = (hash: string) => {
    if (location.pathname !== '/') {
      return `/${hash}`;
    }
    return hash;
  };
  const goToQuote = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);
  const collapseCard = useCallback(() => {
    window.dispatchEvent(new Event('materialsCollapse'));
  }, []);
  const resetMaterials = useCallback(() => {
    collapseCard();
    // Also reset active tab to materials
    window.dispatchEvent(new Event('materialsReset'));
  }, [collapseCard]);
  // Navigate straight to a product's detail page from the Shop dropdown.
  // Previously this routed everything through the home-page Materials Hub
  // (/?cat=…&prod=…#materials), which meant clicking a specific product
  // from any non-home page dumped the customer at the hub instead of the
  // product they actually wanted. Now we go directly to /shop/:slug.
  const goToProduct = useCallback((slug: string) => {
    navigate(`/shop/${slug}`);
  }, [navigate]);
  return <nav className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-[#ffffff] dark:bg-black backdrop-blur-md shadow-lg' : 'bg-[#ffffff] dark:bg-black/75 backdrop-blur-md'}`} style={{
    overflow: navRetracted && !isOpen ? 'hidden' : 'visible',
    height: navRetracted && !isOpen ? isMobileNav ? '64px' : '90px' : isMobileNav ? '80px' : '113px',
    transition: 'height 0.3s ease'
  }} data-id="element-1289" data-mp="1vejxk2xttn5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1290" data-mp="28bmp0a0e80xs">
        <div className="flex justify-between items-center" style={{
        height: navRetracted && !isOpen ? isMobileNav ? '64px' : '90px' : isMobileNav ? '80px' : '113px',
        transition: 'height 0.3s ease'
      }} data-id="element-1291" data-mp="2d9fwd6dfn0zn">
          <div className="flex-shrink-0 flex items-center gap-4" data-id="element-1292" data-mp="2mamdnhnwofty">
            <Link to="/" onClick={resetMaterials} className="relative" aria-label="206Gravel home" data-id="element-1293" data-mp="2r7cy0h6qtxl6">
              <motion.div className="origin-left" initial={{
              opacity: 0,
              scale: 1
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              opacity: {
                duration: 0.4,
                ease: 'easeOut'
              }
            }} data-id="element-1294" data-mp="2w6h6t6pr9taz">
                {!LOGOS_HIDDEN && (
              /* Region-pinned wordmark: shows "253GRAVEL" / "206GRAVEL" /
              "425GRAVEL" based on the visitor's domain. No rotation —
              the navbar reads as a fixed regional identity. */
              /* Full wordmark at the top of the page, green "206" glyph once
              scrolled. Keyed off `isScrolled` rather than `navRetracted`,
              since retraction is disabled above 1300px and desktop would
              otherwise never swap. */
              /* Sized to the largest the bar allows: the retracted heights (64px
              mobile / 90px desktop) are the binding constraint, so h-12 / h-16
              fills them while leaving ~8px of breathing room. */
              <Logo pinned imgClassName="h-[55px] max-sm:h-[35px] w-auto" invertOnLight tinted={isScrolled && !isOpen} data-id="element-1295" data-mp="27ky5d8upuuot" />)}
              </motion.div>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8" data-id="element-1296" data-mp="d6dkow6eaz5f">
            {/* Shop Dropdown */}
            <div className="relative" onMouseEnter={() => setIsShopOpen(true)} onMouseLeave={() => setIsShopOpen(false)} data-id="element-1297" data-mp="87k8r86rbg7d">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} data-id="element-1298" data-mp="2jue1ewe6c3th">
                {t('nav.shop')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1299" data-mp="2a3t6pk1mfjfu" />
              </button>

              <AnimatePresence data-id="element-1300" data-mp="2f3ff9g6i8bib">
                {isShopOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-5 z-50" style={{
                width: '620px'
              }} data-id="element-1301" data-mp="2f24g5to07rxj">
                    <div className="grid grid-cols-3 gap-5" data-id="element-1302" data-mp="3qiuc8jypr3df">
                      {categories.map(cat => <div key={cat.name} data-id="element-1303" data-mp="2e9r6jrtepcii">
                          <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2`} data-id="element-1304" data-mp="2e8plqvml9bnl">
                            {cat.name}
                          </p>
                          <div className="space-y-1.5" data-id="element-1305" data-mp="24j5zrh3kx1oi">
                            {cat.products.map(prod => <button key={prod.slug} onClick={() => {
                        setIsShopOpen(false);
                        goToProduct(prod.slug);
                      }} className={`block text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors text-left w-full px-2 py-1.5 -mx-2 rounded-md`} data-id="element-1306" data-mp="1zl4ecpiokq9v">
                                {prod.name}
                              </button>)}
                          </div>
                        </div>)}
                    </div>

                    <div className="mt-6 border-t border-stone-200 pt-4 dark:border-white/10" data-id="element-1307" data-mp="3fx09zhfptewb">
                      <Link to="/projects/new-gravel-driveway" onClick={() => {
                    setIsShopOpen(false);
                    collapseCard();
                  }} className="group flex items-center justify-between rounded-full border-2 border-[#04A05C] bg-[#d8ecdb] px-4 py-3 text-[#04A05C] transition-colors hover:bg-[#c8e5ce] dark:border-[#22c95c] dark:bg-[#04A05C] dark:text-white dark:hover:bg-[#05894e]" data-id="element-1308" data-mp="3axx5by3ndaov">
                        <div data-id="element-1309" data-mp="2zkwsggr3lciu">
                          <span className="block text-sm font-bold text-[#04A05C] dark:text-white" data-id="element-1310" data-mp="2zlq4nx8tsu8d">
                            Shop by Project
                          </span>
                          <span className="mt-0.5 block text-xs text-[#05894e] dark:text-white/80" data-id="element-1311" data-mp="39ib9oikui5c7">
                            Driveway, patio, drainage & more
                          </span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-[#04A05C] transition-transform group-hover:translate-x-1 dark:text-white" data-id="element-1312" data-mp="ork9975i2enj" />
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Resources Dropdown. Holds the Calculator & Quiz, Compare
             Materials, DIY Guide, Delivery Prep Guide, and Contractors
             Zone links — most of which are reference content, not
             decision tools. Labelled "Resources" so it matches the
             actual contents (and matches the mobile section header). */}
            <div className="relative" onMouseEnter={() => setIsHelpOpen(true)} onMouseLeave={() => setIsHelpOpen(false)} data-id="element-1313" data-mp="3q59h6rjo5m4v">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} data-id="element-1314" data-mp="3u73eqqykia0t">
                {t('nav.resources')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1315" data-mp="1ee4cgragn1os" />
              </button>
              <AnimatePresence data-id="element-1316" data-mp="1eg8grac9u2pl">
                {isHelpOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute left-0 mt-2 w-56 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md rounded-xl shadow-xl border border-stone-200 dark:border-white/20 overflow-hidden" data-id="element-1317" data-mp="1jcfon8o89oa6">
                    <div className="py-2" data-id="element-1318" data-mp="2l5b9helf93b6">
                      <Link to="/help" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1319" data-mp="2l633ikzdeszc">
                        Calculator & Quiz
                      </Link>
                      <Link to="/compare" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1320" data-mp="268sstdujxr4o">
                        {t('nav.compareMaterials')}
                      </Link>
                      <Link to="/delivery-prep" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1321" data-mp="1yvkyo20a6ep8">
                        Delivery Prep Guide
                      </Link>
                      <Link to="/blog" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1322" data-mp="1ty1kt37hw3xt">
                        Blog
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* About Dropdown */}
            <div className="relative" onMouseEnter={() => setIsAboutOpen(true)} onMouseLeave={() => setIsAboutOpen(false)} data-id="element-1323" data-mp="2cm6ibsxk6so6">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} data-id="element-1324" data-mp="x93q1kzqw60n">
                {t('nav.about')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1325" data-mp="oq0dy0kd77as" />
              </button>
              <AnimatePresence data-id="element-1326" data-mp="opqivykgq9y8">
                {isAboutOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-4 z-50 w-48" data-id="element-1327" data-mp="13nsoeeb215i4">
                    <Link to="/about" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors px-2 py-1.5 -mx-2 rounded-md`} data-id="element-1328" data-mp="fxyf26py1z2m">
                      About Us
                    </Link>
                    <Link to="/local-vs-national" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors px-2 py-1.5 -mx-2 rounded-md`} data-id="element-1329" data-mp="2qoc3s9wnotwy">
                      Why Shop Local
                    </Link>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/contact" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-1330" data-mp="3eefz7tzoxncv">
              Contact
            </Link>

            <div className="hidden md:flex items-center space-x-6" data-id="element-1331" data-mp="3jawx3fwozg35">
              <div className="flex items-center space-x-3" data-id="element-1332" data-mp="1k1q4ja1i0a4f">
                <Link to="/delivery-zones" onClick={collapseCard} aria-label="Delivery Zones" title="Delivery Zones" className="relative w-9 h-9 rounded-full flex items-center justify-center text-stone-800 dark:text-white hover:text-brand-orange transition-colors group" data-id="element-1333" data-mp="1oyqcjqhwrmy6">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-1334" data-mp="17pxgshmhdc9i">
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#5c5c5c" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1335" data-mp="17nl1kc83xg05">
                      <circle cx="12" cy="12" r="10" fill="none" data-id="element-1336" data-mp="20cify4976snm" />
                    </svg>
                  </div>
                  <motion.div className="absolute inset-1 pointer-events-none" animate={{
                  rotate: 360
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'linear'
                }} style={{
                  background: isDark ? 'conic-gradient(from 0deg, rgba(255,255,255,0.5) 0deg, rgba(255,255,255,0) 90deg, transparent 360deg)' : 'conic-gradient(from 0deg, rgba(41,37,36,0.5) 0deg, rgba(41,37,36,0) 90deg, transparent 360deg)',
                  WebkitMaskImage: 'radial-gradient(circle, black 70%, transparent 71%)',
                  maskImage: 'radial-gradient(circle, black 70%, transparent 71%)',
                  borderRadius: '9999px'
                }} data-id="element-1337" data-mp="1qgf4g7sghho0" />
                </Link>
                <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-1338" data-mp="7jtzss29a8oe" />
              </div>
              <div className="h-6 w-px bg-stone-200 dark:bg-white/20" data-id="element-1339" data-mp="3puffug9qch2v" />
              {/* Desktop Cart Button */}
              <button onClick={toggleCart} className={`relative flex items-center gap-2 transition-all ${totalItems > 0 ? 'pl-2.5 pr-3.5 py-1.5 rounded-full bg-stone-200/80 dark:bg-white/10 hover:bg-stone-300/80 dark:hover:bg-white/15 text-stone-900 dark:text-white' : 'p-2 text-stone-800 dark:text-white hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, subtotal ${totalPrice.toLocaleString()}` : 'Open cart'} data-id="element-1340" data-mp="3uo9w19q4mdx7">
                <div className="relative" data-id="element-1341" data-mp="2sb761lsxu9yc">
                  <Truck className="w-5 h-5" data-id="element-1342" data-mp="2sc0celnzixfy" />
                  <AnimatePresence data-id="element-1343" data-mp="2nc49yquxre2i">
                    {totalItems > 0 && <motion.span initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-1344" data-mp="2ndf92ddfrxna">
                        {totalItems}
                      </motion.span>}
                  </AnimatePresence>
                </div>
                <AnimatePresence data-id="element-1345" data-mp="3fzqatrcs44lo">
                  {totalItems > 0 && <motion.span initial={{
                  opacity: 0,
                  width: 0
                }} animate={{
                  opacity: 1,
                  width: 'auto'
                }} exit={{
                  opacity: 0,
                  width: 0
                }} className="text-sm font-bold whitespace-nowrap overflow-hidden" data-id="element-1346" data-mp="3g11dtoyeh8bq">
                      ${totalPrice.toLocaleString()}
                    </motion.span>}
                </AnimatePresence>
              </button>
            </div>
          </div>

          <div className="md:hidden flex items-center space-x-3" data-id="element-1347" data-mp="196cxjb1pcpu6">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-1348" data-mp="1e6qu9wxusdcw" />
            {/* Mobile Cart Button */}
            <button onClick={toggleCart} className={`relative flex items-center gap-1.5 transition-all ${totalItems > 0 ? 'pl-2 pr-2.5 py-1.5 rounded-full bg-stone-200/80 dark:bg-white/10 text-stone-900 dark:text-white' : 'p-2 text-stone-800 dark:text-white hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, subtotal ${totalPrice.toLocaleString()}` : 'Open cart'} data-id="element-1349" data-mp="gl3aqcmhxpis">
              <div className="relative" data-id="element-1350" data-mp="3ty50g2jzzuoy">
                <Truck className="w-5 h-5" data-id="element-1351" data-mp="2rkcirp8p2w5" />
                <AnimatePresence data-id="element-1352" data-mp="2qj1gn884gef">
                  {totalItems > 0 && <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} exit={{
                  scale: 0
                }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-1353" data-mp="3reie1ah4gqlw">
                      {totalItems}
                    </motion.span>}
                </AnimatePresence>
              </div>
              {totalItems > 0 && <span className="text-xs font-bold whitespace-nowrap" data-id="element-1354" data-mp="33nushmeaa2gv">
                  ${totalPrice.toLocaleString()}
                </span>}
            </button>

            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-800 dark:text-white hover:text-brand-orange focus:outline-none p-2" aria-label={isOpen ? 'Close menu' : 'Open menu'} data-id="element-1355" data-mp="2yn7478hfvzqc">
              {isOpen ? <X className="h-6 w-6" data-id="element-1356" data-mp="2osyd8p824eua" /> : <Menu className="h-6 w-6" data-id="element-1357" data-mp="2kp0cka332vz4" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-1358" data-mp="19mlr0aeggptn">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setIsOpen(false)} className="md:hidden fixed inset-0 bg-black/40 z-[9998]" data-id="element-1359" data-mp="14mze622jhbld" />
            {/* Pull-down menu */}
            <motion.div initial={{
          y: '-100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '-100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="md:hidden fixed top-0 left-0 right-0 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md shadow-2xl z-[9999] overflow-y-auto max-h-screen" data-id="element-1360" data-mp="qnhl3ai7nc67">
              <div className="flex justify-end items-center p-6" data-id="element-1361" data-mp="7x9lh2dpz3ur">
                <button onClick={() => setIsOpen(false)} className="text-stone-800 dark:text-white hover:text-brand-orange p-2" aria-label="Close menu" data-id="element-1362" data-mp="7ykr77qq6loi">
                  <X className="w-6 h-6" data-id="element-1363" data-mp="2ejxkj4vb3uw5" />
                </button>
              </div>
              <div className="px-8 pb-10 space-y-7 flex flex-col text-right font-nav" data-id="element-1364" data-mp="2jjaee9kli288">
                {/* Mobile Shop Menu - Collapsible */}
                <div className="py-2 border-b border-stone-200 dark:border-[#2a2a2a]" data-id="element-1365" data-mp="2ojy85307kx65">
                  <button onClick={() => setMobileShopOpen(!mobileShopOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white font-bold gap-2 text-lg" data-id="element-1366" data-mp="2sk8bs90j3bhu">
                    Shop Materials
                    <motion.div animate={{
                  rotate: mobileShopOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1367" data-mp="20rhowp4qiz59">
                      <ChevronDown className="w-5 h-5" data-id="element-1368" data-mp="29ngta1st90eu" />
                    </motion.div>
                  </button>
                  <AnimatePresence data-id="element-1369" data-mp="1d1x3lzl3zpst">
                    {mobileShopOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1370" data-mp="1h4jmhazsy0dv">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-4 pr-1" data-id="element-1371" data-mp="2p6z0o3r78cx8">
                          {categories.map(cat => <div key={cat.name} data-id="element-1372" data-mp="21ipidbt727au">
                              <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2 text-right`} data-id="element-1373" data-mp="21jhd6keyw102">
                                {cat.name}
                              </p>
                              <div className="space-y-3" data-id="element-1374" data-mp="26gr3s3e3eiph">
                                {cat.products.map(prod => <button key={prod.slug} onClick={() => {
                          setIsOpen(false);
                          goToProduct(prod.slug);
                        }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right w-full py-1.5" data-id="element-1375" data-mp="26j3t51m6jvck">
                                    {prod.name}
                                  </button>)}
                              </div>
                            </div>)}
                        </div>

                        <div className="mt-4 space-y-2 border-t border-stone-200 pt-3 dark:border-white/10" data-id="element-1376" data-mp="3kgca5udopt8j">
                          <Link to="/projects/new-gravel-driveway" onClick={() => {
                      setIsOpen(false);
                      collapseCard();
                    }} className="flex items-center justify-between rounded-full border-2 border-[#04A05C] bg-[#d8ecdb] px-4 py-3 text-[#04A05C] transition-colors hover:bg-[#c8e5ce] dark:border-[#22c95c] dark:bg-[#04A05C] dark:text-white dark:hover:bg-[#05894e]" data-id="element-1377" data-mp="c0skf5c82sy8">
                            <div className="flex-1 text-right" data-id="element-1378" data-mp="uyby4m9mkzqz">
                              <span className="block text-sm font-bold text-[#04A05C] dark:text-white" data-id="element-1379" data-mp="zptq4kto7pzt">
                                Shop by Project
                              </span>
                              <span className="mt-0.5 block text-xs text-[#05894e] dark:text-white/80" data-id="element-1380" data-mp="3382zrk3ty58z">
                                Driveway, patio, drainage & more
                              </span>
                            </div>
                            <ArrowRight className="ml-3 h-4 w-4 text-[#04A05C] dark:text-white" data-id="element-1381" data-mp="2tayowgffpw7h" />
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div data-id="element-1382" data-mp="3m0x5rojh6n12">
                  <button onClick={() => setMobileHelpOpen(!mobileHelpOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1383" data-mp="3h7kv6ibmsgrb">
                    {t('nav.resources')}
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileHelpOpen ? 'rotate-180' : ''}`} data-id="element-1384" data-mp="dku9eextcks1" />
                  </button>
                  <AnimatePresence data-id="element-1385" data-mp="h16y8bx7ydai">
                    {mobileHelpOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1386" data-mp="h2rrzzbpls7b">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-1387" data-mp="zxgijgyps02y">
                          <Link to="/help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1388" data-mp="14034504skorp">
                            Calculator & Quiz
                          </Link>
                          <Link to="/compare" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1389" data-mp="1npwzfo6ptm0o">
                            Compare Materials
                          </Link>
                          <Link to="/delivery-prep" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1390" data-mp="12yhzze0dt18b">
                            Delivery Prep Guide
                          </Link>
                          <Link to="/blog" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1391" data-mp="1lrc7xnzl7t31">
                            Blog
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Mobile About Menu - Collapsible */}
                <div data-id="element-1392" data-mp="1y84u0cp57whx">
                  <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1393" data-mp="1y7bthtgs2388">
                    About
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} data-id="element-1394" data-mp="1abqz1y9f8976" />
                  </button>
                  <AnimatePresence data-id="element-1395" data-mp="1fau25d5uc5ci">
                    {mobileAboutOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1396" data-mp="34oqnmgvs4dql">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-1397" data-mp="1non497t4vq6h">
                          <Link to="/about" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1398" data-mp="1nn2dzuduqtef">
                            About Us
                          </Link>
                          <Link to="/local-vs-national" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1399" data-mp="14sez0404ffb6">
                            Why Shop Local
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <Link to="/contact" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1400" data-mp="3be1h5le0gpmx">
                  Contact
                </Link>
                <Link to="/delivery-zones" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1401" data-mp="2noyxk7ztgmps">
                  Delivery Zones
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}